import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { logoutProcessor } from "./helpers/logoutProcessor";

import SorterTwoOrderDisplay from "./sorterTwo/sorter-2-order-display";
import SorterTwoSingleOrderDisplay from "./sorterTwo/sorter-2-single-order-display";

import { Order } from "../types/Sorters";
import SorterTwoSelectBay from "./sorterTwo/sorter-2-select-bay";
import { apiFetch } from "../api/apiFetch";
import { useTranslation } from "../i18n";
import { zoneDispatchReasonKey } from "./sorterTwo/zoneDispatchReasons";


const POLL_MS = 1000;

const ZoneStorageSorter2Unloader = ({ warehouse, resetFunc }: { warehouse: string; resetFunc: () => void }) => {
    const navigator = useNavigate();
    const { t } = useTranslation();
    const [availableOrders, set_availableOrders] = useState<Order[]>([]);
    const [selectedOrder, set_selectedOrder] = useState<Order>();
    const [selectedBay, set_selectedBay] = useState<string>("");

    // Function to get available to pick orders
    const getAvailableOrders = useCallback(async () => {
        await apiFetch(`/zonestorage/orders/get-collection-orders`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ warehouse: warehouse })
        }).then(async (res): Promise<any> => {
            let response = await res.json();

            if (res.ok) {
                set_availableOrders(response);
            } else if (res.status === 401) {
                console.log("INVALID PERMISSIONS");
                logoutProcessor();
                navigator("/login");
            } else {
                console.log(response.message);
                toast.error(response.message);
            }
        });
    }, [warehouse, navigator]);

    // Poll for available orders
    useEffect(() => {
        getAvailableOrders();
        const interval = setInterval(getAvailableOrders, POLL_MS);
        return () => clearInterval(interval);
    }, [getAvailableOrders]);

    // Select order to work on
    const selectOrder = ({ order }: { order: Order }) => {
        set_selectedOrder(order);
    };

    // Close order
    const closeOrder = async (completedOrder: boolean, tag: string) => {

        if (completedOrder === false) {
            set_selectedOrder(undefined);
        } else {
            const orderNumber = selectedOrder?.OrderNumber;

            // Tell backend that order is collected
            await apiFetch(`/zonestorage/orders/mark-collected`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ warehouse: warehouse, bay: selectedBay, orderNumber: orderNumber, tag: tag })
            }).then(async (res): Promise<any> => {
                let response = await res.json();
                console.log(response);
                console.log(typeof response);

                if (res.ok) {

                    // Every unit was missing at sort: Portal flagged the
                    // order for a leader and bound no tote. The order is
                    // still closed here; tell the operator why nothing
                    // went to dispatch.
                    if (response && typeof response === "object" && response.flagged === true) {
                        toast.dismiss();
                        toast.info(t("zone_order_flagged_missing", { order: orderNumber ?? "" }));
                    }
                    set_selectedOrder(undefined);

                } else if (res.status === 401) {
                    console.log("INVALID PERMISSIONS");
                    logoutProcessor();
                    navigator("/login");
                } else {
                    // Portal-dispatch refusals (zone_dispatch_via_portal) come
                    // back as { ok:false, reason } on 404/409 — name the reason;
                    // legacy failures carry a message or a bare string.
                    console.log(response);
                    toast.dismiss();
                    if (response && typeof response === "object" && response.reason) {
                        toast.error(t(zoneDispatchReasonKey(response.reason), {
                            status: response.status ?? "",
                            order: orderNumber ?? "",
                        }));
                    } else if (typeof response === "string") {
                        toast.error(response);
                    } else {
                        toast.error(response?.message ?? t("scan_failed"));
                    }
                }
            });
        }

        // Refresh orders
        getAvailableOrders();
    };

    const closeBay = () => {
        set_selectedBay("");
    };

    const refreshBayList = () => {
        getAvailableOrders();
    };


    return (
        <div>
            {(selectedBay === "") && <SorterTwoSelectBay orders={availableOrders} set_selectedBay={set_selectedBay} refreshList={refreshBayList} goBack={resetFunc} />}
            {(availableOrders.length > 0 && selectedBay !== "" && (selectedOrder === null || selectedOrder === undefined)) && <SorterTwoOrderDisplay availableOrders={availableOrders} selectedBay={selectedBay} selectOrder={selectOrder} closeBay={closeBay} />}
            {(selectedOrder !== null && selectedOrder !== undefined) && <SorterTwoSingleOrderDisplay order={selectedOrder} closeOrder={closeOrder} />}
        </div>
    );
};

export default ZoneStorageSorter2Unloader;